import { trigger, transition, style, animate } from '@angular/animations';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import { Component, computed, inject, input, OnInit, signal } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons';

import { map } from 'rxjs';

import { MediaService } from '@eiq/files';
import { MediaView } from '@eiq/files/models';
import { ConfirmDialogComponent, SnackbarMode } from '@eiq/ui';

import { FileType, FileTypeTexts } from '@empathy/global/data';
import { IMediaForm, MediaForm, Question } from '@empathy/survey-core';

@Component({
  selector: 'empathy-file-upload-question-view',
  templateUrl: 'file-upload-question-view.component.html',
  styleUrls: ['./file-upload-question-view.component.scss', './_file-upload-question-view-theme.component.scss'],
  animations: [trigger('mediaInserted', [transition(':enter', [style({ opacity: 0 }), animate('1500ms', style({ opacity: 1 }))])])]
})
export class FileUploadQuestionViewComponent implements OnInit {
  question = input<Question>();
  questionCtrl = input<FormArray<FormGroup<IMediaForm>>>();

  acceptedTypes$ = computed(() => {
    if (this.question().fileTypes?.length > 0) {
      return this.question().fileTypes;
    }
    return this.defaultFileTypes;
  });
  acceptedTypeTexts$ = computed(() => {
    if (this.question().fileTypes?.length > 0) {
      return this.question().fileTypes.map((fileType) => FileTypeTexts[fileType]);
    }
    return this.defaultFileTypes.map((fileType) => FileTypeTexts[fileType]);
  });
  maxSize$ = computed(() => {
    if (this.question().maxSize > 0) {
      return this.question().maxSize;
    }
    return this.defaultMaxSize;
  });

  showUploader$ = signal(false);
  mediaUploaded$ = signal<MediaView[]>([]);
  mediaFailed$ = signal<MediaView[]>([]);
  uploading$ = signal(false);

  faTrashCan = faTrashCan;

  private dialog = inject(MatDialog);
  private mediaService = inject(MediaService);
  private snackbar = inject(MatSnackBar);

  private readonly defaultFileTypes = [FileType.ImagePng, FileType.ImageGif, FileType.ImageJpeg];
  private readonly defaultMaxSize = 25;

  ngOnInit(): void {
    if (this.questionCtrl().value && this.questionCtrl().value.length > 0) {
      for (const media of this.questionCtrl().value) {
        this.mediaUploaded$.update((mediaUploaded) => [
          ...mediaUploaded,
          {
            url$: this.mediaService.getMediaUrl(media.mediaId).pipe(map((m) => m.url)),
            id: media.mediaId,
            type: media.type as FileType,
            name: media.name
          }
        ]);
      }
    }
  }

  onUploadMedia(file: File): void {
    const fileData = new FormData();
    fileData.append('mediaFile', file);
    fileData.append('itemReference', this.question().id);

    this.uploading$.set(true);
    this.mediaService.uploadStimulus(fileData).subscribe((mediaEvent: HttpEvent<{ id: string }>) => {
      if (!mediaEvent) {
        this.uploading$.set(false);
        this.snackbar.open(`There was an error uploading the file: ${file.name}`, 'ok', { duration: 3000, panelClass: SnackbarMode.Error });
        return;
      }

      if ((mediaEvent as any).body) {
        this.questionCtrl().push(new MediaForm({ mediaId: (mediaEvent as any).body.id, name: file.name, type: file.type as FileType }).form);

        this.mediaUploaded$.update((mediaUploaded) => {
          mediaUploaded.push({
            url$: this.mediaService.getMediaUrl((mediaEvent as any).body.id).pipe(map((media) => media.url)),
            id: (mediaEvent as any).body.id,
            type: file.type as FileType,
            name: file.name
          });

          return [...mediaUploaded];
        });

        this.showUploader$.set(false);
      }

      if (mediaEvent.type === HttpEventType.Response) {
        this.uploading$.set(false);
      }
    });
  }

  onDelete(mediaId: string): void {
    const confirmationRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Remove File',
        confirmText: 'Are you sure you want to remove this file?',
        confirmBtn: 'Remove',
        color: 'warn'
      }
    });
    confirmationRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        const mediaUploaded = this.questionCtrl();
        let index = mediaUploaded.controls.findIndex((control) => control.get('mediaId').value === mediaId);
        mediaUploaded.removeAt(index);

        index = this.mediaUploaded$().findIndex((s) => s.id === mediaId);

        this.mediaUploaded$.update((media) => {
          const indexToDelete = media.findIndex((s) => s.id === mediaId);
          media[indexToDelete] = { ...media[indexToDelete], deleted: true };

          return [...media];
        });

        this.mediaService.deleteStimulus(mediaId).subscribe();
      }
    });
  }

  onDeleteFailed(mediaId: string): void {
    this.mediaFailed$.update((media) => {
      const indexToDelete = media.findIndex((s) => s.id === mediaId);
      media[indexToDelete] = { ...media[indexToDelete], deleted: true };

      return [...media];
    });
  }
}
